import {useEffect, useRef, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import EntityTable from '../components/Listing/EntityTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCoins,
  faEye,
  faGear,
  faPenToSquare,
  faPrint,
  faUserSlash
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { formatRelativeDate, isToday } from '../utils/FormatDate';
import { eventBus } from '../bus/eventBus';
import { NavLink } from 'react-router-dom';
import PatientImage from '../components/Patient/PatientImage';
import { Document, Page, pdfjs } from 'react-pdf';
import Modal from '../components/Modal/Modal';
import InputText from '../components/Form/Fields/InputText';
import Select from '../components/Form/Fields/Select';
import InputPatient from '../components/Form/Fields/InputPatient';
import SendInvoiceEmailModal from '../components/Modal/SendInvoiceEmailModal';
import useInvoiceColumns from "../hooks/invoiceColumns";
import { formatAmount } from "../components/Order/invoiceColumns";
import PaymentMethodBadge from "../components/Order/PaymentMethodBadge";

import 'react-pdf/dist/Page/TextLayer.css';
import LoadingBar from "react-top-loading-bar";
import {formatPrice} from "../utils/FormatPrice";
import { API_BASE_URL } from '../api';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

export default function Invoices() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('desc');
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const { setTitle, setTitleIcon } = usePageTitle();
  const ref = useRef(null);

  const [pdfData, setPdfData] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [emailTarget, setEmailTarget] = useState(null);

  useEffect(() => {
    setTitle(`${t('Invoices')}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">${t('invoices.subtitle')}</span>`);
    document.title = t('Invoices');
    setTitleIcon('ti-file-invoice');
    return () => setTitleIcon(null);
  }, [setTitle, setTitleIcon, t]);

  useBreadcrumbSetup([
    { label: t('Invoices'), url: "/invoices" }
  ]);

  /*useEffect(() => {
    eventBus.on('orderCreated', () => {
      fetchInvoices();
    });
    return () => {
      eventBus.off('orderCreated');
    };
  }, []);*/

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const columns = useInvoiceColumns({
    setPdfData,
    setShowPdfModal,
    setPageNumber,
    setEmailTarget
  });

  // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
  // — reuses the actions column's own dropdown so both views offer identical actions.
  const actionsColumn = columns.find((c) => c.accessor === "actions");
  const renderInvoiceCard = (row) => (
    <div className="card h-100 shadow-sm">
      <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
        <NavLink to={`/invoices/${row.id}`} style={{ fontSize: 13.5, fontWeight: 600, color: "#111827" }}>
          {row.invoiceNumber}
        </NavLink>
        <span style={{ fontSize: 11.5, color: "#9ca3af" }}>{formatRelativeDate(row.invoiceCreatedAt)}</span>

        {row.patient ? (
          <div className="d-flex align-items-center gap-2" style={{ marginTop: 2 }}>
            <PatientImage patient={row.patient} size={28} />
            <span style={{ fontSize: 12.5, color: "#374151" }}>
              {row.patient.firstName}{row.patient.lastName ? ` ${row.patient.lastName[0]}.` : ''}
            </span>
          </div>
        ) : (
          <span style={{ fontSize: 11.5, color: "#9ca3af" }}>Visiteur</span>
        )}

        {row.paymentMethod && (
          <div style={{ alignSelf: "flex-start" }}><PaymentMethodBadge method={row.paymentMethod} /></div>
        )}

        <div className="d-flex align-items-center justify-content-between" style={{ marginTop: "auto", paddingTop: 6 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{formatAmount(row.totalAmount)}</span>
            <span style={{ fontSize: 10.5, color: "#9ca3af", fontWeight: 500 }}>dhs</span>
          </div>
          {actionsColumn?.Cell(row)}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <EntityTable
        endpoint={`${API_BASE_URL}/api/v1/invoices`}
        columns={columns}
        // today's rows get a light tint, to spot them at a glance (same as the sales listing)
        rowClassName={(row) => (isToday(row.invoiceCreatedAt) ? 'row-today' : '')}
        defaultSort="id"
        defaultDirection="desc"
        pageSize={20}
        emptyMessage={t('invoices.empty')}
        searchable={true}
        searchPlaceholder={t('invoices.search_placeholder')}
        multiSelect={true}
        enableCardView={true}
        renderCard={renderInvoiceCard}
      />

      {/* Modal PDF */}
      <Modal title={t('invoices.pdf_modal_title')} show={showPdfModal} onClose={() => setShowPdfModal(false)} classSize="modal-lg">
          {pdfData ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Document
                  file={pdfData}
                  onLoadSuccess={onDocumentLoadSuccess}
                >
                  <Page pageNumber={pageNumber} width={680} />
                </Document>
              </div>

              {numPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    disabled={pageNumber <= 1}
                    onClick={() => setPageNumber(pageNumber - 1)}
                  >
                    {t('Previous')}
                  </button>
                  <span className="text-muted small">{t('pdf_viewer.page_indicator', { page: pageNumber, total: numPages })}</span>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    disabled={pageNumber >= numPages}
                    onClick={() => setPageNumber(pageNumber + 1)}
                  >
                    {t('Next')}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4 text-muted">{t('invoices.pdf_loading')}</div>
          )}
      </Modal>

      <SendInvoiceEmailModal invoice={emailTarget} onClose={() => setEmailTarget(null)} />
    </>
  );
}
